import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Sparkles, Send, ChevronDown, ChevronRight, AlertTriangle, RotateCcw, Award, ThumbsUp, AlertCircle, ArrowRight, Home, Coins } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { btnPrimary, btnSecondary, btnGhost, card, textarea, label, LEVEL_COLORS } from "@/lib/styles";

function scoreColor(s) {
  if (s >= 80) return "text-emerald-400 border-emerald-500/50 bg-emerald-950/30";
  if (s >= 55) return "text-yellow-400 border-yellow-500/50 bg-yellow-950/30";
  return "text-red-400 border-red-500/50 bg-red-950/30";
}

function ResultCard({ result }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`${card} p-6 mt-3`} data-testid="result-card">
      <div className="flex items-start gap-5">
        <div className={`shrink-0 w-20 h-20 border flex flex-col items-center justify-center ${scoreColor(result.score)}`}>
          <span className="font-sans font-black text-3xl" data-testid="result-score">{result.score}</span>
          <span className="text-[9px] uppercase tracking-widest">/ 100</span>
        </div>
        <div className="flex-1 min-w-0">
          <span className={label}>verdict</span>
          <p className="font-sans font-bold text-lg mt-1" data-testid="result-verdict">{result.verdict}</p>
          {result.gold_earned ? (
            <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-gold border border-gold/30 bg-gold/5 px-2.5 py-1 rounded-md" data-testid="result-loot">
              <Coins size={13} /> +{result.gold_earned} gold · +{result.xp_earned} XP
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-5 border-l-2 border-emerald-500/50 pl-4">
        <span className="text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1"><Sparkles size={11} /> Altaris</span>
        <p className="mt-1 text-sm text-zinc-300 leading-relaxed italic" data-testid="result-feedback">{result.examiner_feedback}</p>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <span className="text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1"><ThumbsUp size={11} /> strengths</span>
          <ul className="mt-2 space-y-1.5">{(result.strengths || []).map((s, i) => <li key={i} className="text-sm text-zinc-300 flex gap-2"><span className="text-emerald-400">+</span>{s}</li>)}</ul>
        </div>
        <div>
          <span className="text-[10px] uppercase tracking-widest text-yellow-400 flex items-center gap-1"><AlertCircle size={11} /> gaps</span>
          <ul className="mt-2 space-y-1.5">{(result.gaps || []).map((s, i) => <li key={i} className="text-sm text-zinc-300 flex gap-2"><span className="text-yellow-400">!</span>{s}</li>)}</ul>
        </div>
      </div>

      <button data-testid="reveal-model-answer" onClick={() => setOpen((o) => !o)} className={`${btnGhost} mt-5`}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} model answer
      </button>
      {open && (
        <div className="mt-2 border border-zinc-800 bg-black p-4" data-testid="model-answer-panel">
          <span className="text-[10px] uppercase tracking-widest text-emerald-400">ideal answer</span>
          <p className="mt-2 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{result.ideal_answer}</p>
          {result.rubric?.length > 0 && (
            <div className="mt-4">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">rubric</span>
              <ul className="mt-2 space-y-1">{result.rubric.map((r, i) => <li key={i} className="text-xs text-zinc-400 flex gap-2"><span className="text-zinc-600">·</span>{r}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default function Interview() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [session, setSession] = useState(null);
  const [turns, setTurns] = useState([]); // {turn_id, question, level, number, answer, result}
  const [answer, setAnswer] = useState("");
  const [loadingQ, setLoadingQ] = useState(false);
  const [grading, setGrading] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const started = useRef(false);

  const active = turns[turns.length - 1];
  const awaiting = active && !active.result;
  const finished = !!active?.result && !!active.of && active.number >= active.of;

  const fetchQuestion = async () => {
    setLoadingQ(true); setError(null);
    try {
      const { data } = await api.post(`/interview/${sessionId}/question`);
      setTurns((t) => [...t, { ...data, answer: null, result: null }]);
      setAnswer("");
    } catch (e) {
      setError("Altaris could not conjure a question. Retry?");
    } finally { setLoadingQ(false); }
  };

  useEffect(() => {
    api.get(`/interview/${sessionId}`).then((r) => setSession(r.data)).catch(() => navigate("/curriculum"));
    if (!started.current) { started.current = true; fetchQuestion(); }
  }, [sessionId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, grading]);

  const submit = async () => {
    if (!answer.trim() || !active) return;
    setGrading(true); setError(null);
    try {
      const { data } = await api.post(`/interview/${sessionId}/grade`, { turn_id: active.turn_id, user_answer: answer });
      setTurns((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, answer, result: data } : x)));
      if (data.gold_earned) toast.success(`+${data.gold_earned} gold · +${data.xp_earned} XP`);
      refreshUser();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Free trial used up. Upgrade to Pro");
        navigate("/pricing");
        return;
      }
      setError("Altaris could not weigh your answer. Retry?");
    } finally { setGrading(false); }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between">
          <Link to={session ? `/topic/${session.topic_id}` : "/curriculum"} className={btnGhost}><Home size={14} /> exit</Link>
          <span className="text-[11px] text-zinc-500">interviewing · {session?.topic_title}</span>
        </div>

        <div className="mt-4 border border-emerald-500/30 bg-emerald-950/10 p-5 scanlines relative flex items-center gap-3">
          <Sparkles className="text-emerald-400" size={22} />
          <div>
            <h1 className="font-sans font-black tracking-tight text-lg">{session?.topic_title || "Mock interview"}</h1>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Tier {session?.tier} · an audience with Altaris</p>
          </div>
        </div>

        <div className="mt-6 space-y-6">
          {turns.map((turn, idx) => (
            <div key={turn.turn_id} data-testid={`turn-${idx}`}>
              {/* examiner question */}
              <div className="border-l-2 border-emerald-500/50 pl-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1"><Sparkles size={11} /> question {turn.number}{turn.of ? ` / ${turn.of}` : ""}</span>
                  <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${LEVEL_COLORS[turn.level] || ""}`}>{turn.level}</span>
                </div>
                <p className="text-base text-zinc-100 leading-relaxed font-medium" data-testid={`question-${idx}`}>{turn.question}</p>
              </div>

              {turn.answer && (
                <div className="mt-3 border border-zinc-800 bg-zinc-900/40 p-4">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">your answer</span>
                  <p className="mt-1 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{turn.answer}</p>
                </div>
              )}

              {turn.result && <ResultCard result={turn.result} />}
            </div>
          ))}

          {loadingQ && <div className="text-emerald-400 font-mono text-sm border-l-2 border-emerald-500/50 pl-4"><span className="cursor-blink">Altaris strokes his beard</span></div>}

          {error && (
            <div className="border border-red-500/40 bg-red-950/20 p-4 flex items-center justify-between gap-3" data-testid="interview-error">
              <span className="text-sm text-red-300 flex items-center gap-2"><AlertTriangle size={16} /> {error}</span>
              <button onClick={awaiting ? submit : fetchQuestion} className={btnSecondary}><RotateCcw size={14} /> Retry</button>
            </div>
          )}
        </div>

        {/* answer composer */}
        {awaiting && !error && (
          <div className="mt-6 sticky bottom-4">
            <span className={label}>type your answer</span>
            <textarea
              data-testid="interview-answer-input"
              className={`${textarea} mt-2`}
              placeholder="Answer like you would out loud in a real interview..."
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              disabled={grading}
            />
            <div className="mt-3 flex justify-end">
              <button data-testid="interview-submit-button" onClick={submit} disabled={grading || !answer.trim()} className={btnPrimary}>
                <Send size={16} /> {grading ? "grading..." : "Submit answer"}
              </button>
            </div>
          </div>
        )}

        {/* next question / interview complete */}
        {active?.result && !error && (
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            {!finished && active.result.follow_up && (
              <div className="text-xs text-zinc-500 border border-zinc-800 bg-zinc-950 p-3 flex-1">
                <span className="text-emerald-400 uppercase tracking-wider">follow-up queued:</span> {active.result.follow_up}
              </div>
            )}
            {finished ? (
              <>
                <div className="text-xs text-zinc-400 border border-emerald-500/30 bg-emerald-950/10 p-3 flex-1" data-testid="interview-complete">
                  <span className="text-emerald-400 uppercase tracking-wider">the audience has ended.</span> Altaris has no further questions. Return when you have studied.
                </div>
                <button data-testid="finish-interview-button" onClick={() => navigate("/dashboard")} className={btnPrimary}>Finish & view stats</button>
              </>
            ) : (
              <>
                <button data-testid="next-question-button" onClick={fetchQuestion} disabled={loadingQ} className={btnPrimary}>
                  Next question <ArrowRight size={16} />
                </button>
                <button data-testid="finish-interview-button" onClick={() => navigate("/dashboard")} className={btnSecondary}>Finish & view stats</button>
              </>
            )}
          </div>
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
